import React, { Fragment, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import RichTextEditor from './RichTextEditor';
import { hasActualTextContent, sanitizeNoteInput } from '../utils/sanitize';

interface ResolutionData {
  resolutionCode: string;
  resolutionNotes: string;
}

interface ResolutionNotesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (data: ResolutionData) => void;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmButtonClass?: string;
  isLoading?: boolean;
}

const ResolutionNotesModal: React.FC<ResolutionNotesModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  confirmButtonClass = 'bg-green-600 hover:bg-green-700 text-white',
  isLoading = false
}) => {
  const [resolutionCode, setResolutionCode] = useState('');
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [error, setError] = useState('');

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setResolutionCode('');
      setResolutionNotes('');
      setError('');
    }
  }, [isOpen]);

  const RESOLUTION_CODE_OPTIONS = [
    { value: 'solved_permanently', label: 'Solved (Permanently)' },
    { value: 'solved_fixed_by_support', label: 'Solved (Fixed by Support)' },
    { value: 'solved_fixed_by_prb', label: 'Solved (Fixed by Problem)' },
    { value: 'solved_workaround_prb', label: 'Solved (Workaround from Problem)' },
    { value: 'solved_by_customer', label: 'Solved (By Customer)' },
    { value: 'solved_fixed_by_change', label: 'Solved (Fixed by Change)' },
    { value: 'solved_fixed_by_incident', label: 'Solved (Fixed by Incident)' },
    { value: 'inconclusive_out_of_scope', label: 'Inconclusive (Out of Scope)' },
    { value: 'inconclusive_cannot_reproduce', label: 'Inconclusive (Cannot Reproduce)' },
    { value: 'inconclusive_no_workaround', label: 'Inconclusive (No Workaround)' },
    { value: 'duplicate_issue', label: 'Duplicate Issue' },
    { value: 'auto_resolved', label: 'Auto Resolved' }
  ];

  const handleConfirm = () => {
    if (!resolutionCode) {
      setError('Resolution code is required');
      return;
    }
    if (!hasActualTextContent(resolutionNotes)) {
      setError('Resolution notes are required');
      return;
    }
    onConfirm({
      resolutionCode,
      resolutionNotes: sanitizeNoteInput(resolutionNotes),
    });
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <CheckCircleIcon className="h-6 w-6 text-green-600" />
                    </div>
                    <div>
                      <Dialog.Title as="h3" className="text-lg font-medium text-gray-900">
                        {title}
                      </Dialog.Title>
                    </div>
                  </div>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-600"
                    disabled={isLoading}
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="mt-2 ml-9">
                  <p className="text-sm text-gray-500 mb-4">
                    {message}
                  </p>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Resolution Code <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={resolutionCode}
                      onChange={(e) => {
                        setResolutionCode(e.target.value);
                        setError('');
                      }}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                      disabled={isLoading}
                    >
                      <option value="">Select resolution code...</option>
                      {RESOLUTION_CODE_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Resolution Notes <span className="text-red-500">*</span>
                    </label>
                    <RichTextEditor
                      value={resolutionNotes}
                      onChange={(value) => {
                        setResolutionNotes(value);
                        setError('');
                      }}
                      minHeight="120px"
                      placeholder="Enter detailed resolution notes explaining how this incident was resolved..."
                      disabled={isLoading}
                    />
                    {error && (
                      <p className="mt-1 text-sm text-red-600">{error}</p>
                    )}
                  </div>
                </div>

                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors"
                    disabled={isLoading}
                  >
                    {cancelLabel}
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirm}
                    className={`px-4 py-2 text-sm font-medium border border-transparent rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 transition-colors ${confirmButtonClass}`}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Processing...' : confirmLabel}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default ResolutionNotesModal;
